import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useItems, useCreateItem, useUpdateItem, useDeleteItem } from '@/hooks/useItems';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import type { Item, Location } from '@/lib/schemas';
import { cn, formatCurrency } from '@/lib/utils';
import { departmentTypeOptions, getStockStatus, getStockStatusLabel, getStockStatusVariant } from '@/lib/seed-data';
import { COMMON_UOMS } from '@/lib/uom';
import {
  Plus,
  Search,
  Package,
  AlertTriangle,
  TrendingUp,
  Filter,
  X,
  AlertCircle,
  CheckCircle,
  Warehouse,
  LayoutGrid,
  List,
  BarChart3,
} from 'lucide-react';

type DepartmentFilter = 'all' | 'general' | 'it';
type StockStatusFilter = 'all' | 'critical' | 'low' | 'normal' | 'overstock';

export default function ItemsPage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id || '550e8400-e29b-41d4-a716-446655440000';

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<DepartmentFilter>('all');
  const [stockStatusFilter, setStockStatusFilter] = useState<StockStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [deletingItem, setDeletingItem] = useState<Item | null>(null);
  const [priceComparisonItem, setPriceComparisonItem] = useState<Item | null>(null);
  const [isPriceComparisonOpen, setIsPriceComparisonOpen] = useState(false);

  const { data: itemsResponse = { data: [], totalCount: 0 }, isLoading, error } = useItems().getItems({
    searchQuery: searchQuery || undefined,
    page,
    limit: pageSize,
  });
  const { data: priceComparisonData, isLoading: priceComparisonLoading } = useItems().getItemPriceComparison(
    priceComparisonItem?.item_id || ''
  );
  const { data: locations = [] } = useLocations();
  const createMutation = useCreateItem();
  const updateMutation = useUpdateItem();
  const deleteMutation = useDeleteItem();
  const { success: toastSuccess, error: toastError } = useToast();

  const items = itemsResponse.data;
  const totalCount = itemsResponse.totalCount;

  const categories = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.category))).sort();
  }, [items]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, categoryFilter, departmentFilter, stockStatusFilter, pageSize]);

  // Apply local filtering
  const filteredItems = useMemo(() => {
    let result = [...items];

    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter((item) => item.category === categoryFilter);
    }

    // Department type filter
    if (departmentFilter !== 'all') {
      result = result.filter((item) => item.department_type === departmentFilter || item.department_type === 'both');
    }

    // Stock status filter
    if (stockStatusFilter !== 'all') {
      result = result.filter((item) => {
        const status = getStockStatus(
          item.quantity,
          item.min_stock_level || 0,
          item.max_stock_level || 0,
          item.safety_stock || 0
        );
        return status === stockStatusFilter;
      });
    }

    return result;
  }, [items, categoryFilter, departmentFilter, stockStatusFilter]);

  // Stats calculations
  const stats = useMemo(() => {
    const total = items.length;
    const critical = items.filter((i) => getStockStatus(i.quantity, i.min_stock_level || 0, i.max_stock_level || 0, i.safety_stock || 0) === 'critical').length;
    const low = items.filter((i) => getStockStatus(i.quantity, i.min_stock_level || 0, i.max_stock_level || 0, i.safety_stock || 0) === 'low').length;
    const normal = items.filter((i) => getStockStatus(i.quantity, i.min_stock_level || 0, i.max_stock_level || 0, i.safety_stock || 0) === 'normal').length;
    const overstock = items.filter((i) => getStockStatus(i.quantity, i.min_stock_level || 0, i.max_stock_level || 0, i.safety_stock || 0) === 'overstock').length;
    const generalItems = items.filter((i) => i.department_type === 'general' || i.department_type === 'both').length;
    const itItems = items.filter((i) => i.department_type === 'it' || i.department_type === 'both').length;
    const totalValue = items.reduce((sum, i) => sum + (i.quantity * i.unit_cost), 0);
    
    return { total, critical, low, normal, overstock, generalItems, itItems, totalValue };
  }, [items]);

  const getLocationName = (locationId: string) => {
    const location = locations.find((l) => l.location_id === locationId);
    return location?.name || 'Unknown Location';
  };

  const handleCreate = async (data: Partial<Item>) => {
    try {
      await createMutation.mutateAsync(
        data as Omit<Item, 'item_id' | 'created_at' | 'updated_at'>
      );
      setIsCreateOpen(false);
      toastSuccess('Item created', 'Item has been created successfully.');
    } catch (err: any) {
      toastError('Failed to create item', err.message || 'An unknown error occurred');
    }
  };

  const handleUpdate = async (data: Partial<Item>) => {
    if (editingItem && editingItem.item_id) {
      try {
        await updateMutation.mutateAsync({
          id: editingItem.item_id,
          data: data as Partial<Omit<Item, 'item_id' | 'created_at'>>,
        });
        setEditingItem(null);
        toastSuccess('Item updated', 'Item has been updated successfully.');
      } catch (err: any) {
        toastError('Failed to update item', err.message || 'An unknown error occurred');
      }
    }
  };

  const handleDelete = async () => {
    if (deletingItem && deletingItem.item_id) {
      try {
        await deleteMutation.mutateAsync(deletingItem.item_id);
        setDeletingItem(null);
        toastSuccess('Item deleted', 'Item has been deleted successfully.');
      } catch (err: any) {
        toastError('Failed to delete item', err.message || 'An unknown error occurred');
      }
    }
  };

  const locationOptions = locations
    .filter((loc): loc is Location => !!loc.location_id)
    .map((loc) => ({
      value: loc.location_id!,
      label: `${loc.code} - ${loc.name}`,
    }));

  const itemFields = [
    {
      name: 'hospital_id',
      label: 'Hospital ID',
      type: 'number' as const,
      required: true,
      placeholder: 'Enter hospital ID',
    },
    {
      name: 'location_id',
      label: 'Location',
      type: 'select' as const,
      required: true,
      options: locationOptions,
      helperText: 'Select the storage location for this item',
    },
    {
      name: 'name',
      label: 'Item Name',
      type: 'text' as const,
      required: true,
      placeholder: 'Enter item name',
    },
    {
      name: 'code',
      label: 'Item Code',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., ATK-001',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'Enter item description (optional)',
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., ATK',
    },
    {
      name: 'department_type',
      label: 'Department Type',
      type: 'select' as const,
      required: true,
      options: departmentTypeOptions,
      helperText: 'Select which department this item belongs to',
    },
    {
      name: 'unit_of_measure',
      label: 'Base Unit (UOM)',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., pcs, lembar, unit',
      helperText: 'Smallest unit for stock tracking (e.g., pcs, lembar, unit)',
    },
    {
      name: 'purchase_uom',
      label: 'Purchase UOM',
      type: 'select' as const,
      options: COMMON_UOMS,
      placeholder: 'Select purchase unit',
      helperText: 'Unit when buying (e.g., box, rim)',
    },
    {
      name: 'purchase_uom_qty',
      label: 'Purchase Qty',
      type: 'number' as const,
      placeholder: 'e.g., 500',
      helperText: 'How many base units per purchase unit (e.g., 500 lembar per rim)',
    },
    {
      name: 'issue_uom',
      label: 'Issue UOM',
      type: 'select' as const,
      options: COMMON_UOMS,
      placeholder: 'Select issue unit',
      helperText: 'Unit when requesting (e.g., pcs, pack)',
    },
    {
      name: 'issue_uom_qty',
      label: 'Issue Qty',
      type: 'number' as const,
      placeholder: 'e.g., 1',
      helperText: 'How many base units per issue unit',
    },
    {
      name: 'quantity',
      label: 'Initial Quantity',
      type: 'number' as const,
      required: true,
      placeholder: 'Enter current quantity',
    },
    {
      name: 'unit_cost',
      label: 'Unit Cost (IDR)',
      type: 'number' as const,
      required: true,
      placeholder: 'Enter cost per unit',
    },
    {
      name: 'min_stock_level',
      label: 'Min Stock Level',
      type: 'number' as const,
      placeholder: 'Minimum stock threshold',
      helperText: 'Alert when stock falls below this level',
    },
    {
      name: 'max_stock_level',
      label: 'Max Stock Level',
      type: 'number' as const,
      placeholder: 'Maximum stock threshold',
      helperText: 'Alert when stock exceeds this level',
    },
    {
      name: 'safety_stock',
      label: 'Safety Stock',
      type: 'number' as const,
      placeholder: 'Buffer stock level',
      helperText: 'Minimum buffer stock to maintain',
    },
    {
      name: 'lead_time_days',
      label: 'Lead Time (Days)',
      type: 'number' as const,
      placeholder: 'e.g., 7',
      helperText: 'Supplier delivery time in days',
    },
    {
      name: 'is_critical',
      label: 'Critical Item',
      type: 'checkbox' as const,
      helperText: 'Mark as critical item (cannot run out)',
    },
    {
      name: 'is_active',
      label: 'Active',
      type: 'checkbox' as const,
      helperText: 'Whether this item is active in inventory',
    },
  ] as const satisfies FormField[];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Items Catalog
          </h1>
          <p className="text-text-secondary mt-1">
            Manage inventory items - {stats.generalItems} General, {stats.itItems} IT
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Item
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading items</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className={cn(stats.critical > 0 ? "border-red-300" : "")}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className={cn("h-4 w-4", stats.critical > 0 ? "text-red-600" : "text-gray-400")} />
              <span className="text-xs text-text-muted">Critical</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{stats.critical}</p>
          </CardContent>
        </Card>
        <Card className={cn(stats.low > 0 ? "border-yellow-300" : "")}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className={cn("h-4 w-4", stats.low > 0 ? "text-yellow-600" : "text-gray-400")} />
              <span className="text-xs text-text-muted">Low Stock</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{stats.low}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-xs text-text-muted">Normal</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{stats.normal}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Warehouse className="h-4 w-4 text-blue-600" />
              <span className="text-xs text-text-muted">Overstock</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{stats.overstock}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              <span className="text-xs text-text-muted">Total Items</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-xs text-text-muted">Total Value</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{formatCurrency(stats.totalValue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search items by name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search items"
              />
            </div>
            
            {/* Department Filter */}
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value as DepartmentFilter)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[140px]"
            >
              <option value="all">All Departments</option>
              <option value="general">Umum</option>
              <option value="it">IT</option>
            </select>

            {/* Stock Status Filter */}
            <select
              value={stockStatusFilter}
              onChange={(e) => setStockStatusFilter(e.target.value as StockStatusFilter)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[140px]"
            >
              <option value="all">All Status</option>
              <option value="critical">Critical (Habis)</option>
              <option value="low">Low Stock</option>
              <option value="normal">Normal</option>
              <option value="overstock">Overstock</option>
            </select>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[160px]"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Clear filters */}
            {(departmentFilter !== 'all' || stockStatusFilter !== 'all' || categoryFilter !== 'all' || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                  setDepartmentFilter('all');
                  setStockStatusFilter('all');
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={pageSize} columns={8} />
      ) : filteredItems.length > 0 ? (
        <>
          <DataTable
            columns={[
              {
                key: 'name',
                header: 'Item',
                render: (item: Item) => (
                  <div>
                    <p className="font-medium text-text-primary">{item.name}</p>
                    <p className="text-xs text-text-muted">{item.code}</p>
                  </div>
                ),
              },
              {
                key: 'department_type',
                header: 'Dept',
                render: (item: Item) => (
                  <Badge variant={item.department_type === 'it' ? 'info' : item.department_type === 'both' ? 'success' : 'default'}>
                    {item.department_type === 'general' ? 'Umum' : item.department_type === 'it' ? 'IT' : 'Both'}
                  </Badge>
                ),
              },
              {
                key: 'category',
                header: 'Category',
                render: (item: Item) => (
                  <span className="text-text-secondary text-sm">{item.category}</span>
                ),
              },
              {
                key: 'uom',
                header: 'UOM',
                render: (item: Item) => {
                  const parts = [item.unit_of_measure];
                  if (item.purchase_uom && item.purchase_uom_qty) {
                    parts.push(`Buy: ${item.purchase_uom_qty} ${item.purchase_uom}`);
                  }
                  if (item.issue_uom && item.issue_uom_qty) {
                    parts.push(`Issue: ${item.issue_uom_qty} ${item.issue_uom}`);
                  }
                  return (
                    <span className="text-xs text-text-muted">
                      {parts.join(' | ')}
                    </span>
                  );
                },
              },
              {
                key: 'quantity',
                header: 'Stock',
                render: (item: Item) => {
                  const status = getStockStatus(
                    item.quantity,
                    item.min_stock_level || 0,
                    item.max_stock_level || 0,
                    item.safety_stock || 0
                  );
                  const variant = getStockStatusVariant(status);
                  const label = getStockStatusLabel(status);
                  
                  return (
                    <div className="flex items-center gap-2">
                      <Badge variant={variant} className="min-w-[60px] justify-center">
                        {item.quantity}
                      </Badge>
                      {item.is_critical && <span className="text-red-500" title="Critical Item">*</span>}
                    </div>
                  );
                },
              },
              {
                key: 'stock_status',
                header: 'Status',
                render: (item: Item) => {
                  const status = getStockStatus(
                    item.quantity,
                    item.min_stock_level || 0,
                    item.max_stock_level || 0,
                    item.safety_stock || 0
                  );
                  const variant = getStockStatusVariant(status);
                  const label = getStockStatusLabel(status);
                  
                  return (
                    <Badge variant={variant} className="whitespace-nowrap">
                      {label}
                    </Badge>
                  );
                },
              },
              {
                key: 'min_stock',
                header: 'Min/Max',
                render: (item: Item) => (
                  <span className="text-xs text-text-muted">
                    {(item.min_stock_level || 0)} / {(item.max_stock_level || '-')}
                  </span>
                ),
              },
              {
                key: 'unit_cost',
                header: 'Cost',
                render: (item: Item) => (
                  <span className="font-mono text-text-primary text-sm">
                    {formatCurrency(item.unit_cost)}
                  </span>
                ),
              },
              {
                key: 'is_active',
                header: 'Status',
                render: (item: Item) => (
                  <Badge variant={item.is_active ? 'success' : 'error'}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                ),
              },
              {
                key: 'actions',
                header: 'Actions',
                render: (item: Item) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPriceComparisonItem(item);
                      setIsPriceComparisonOpen(true);
                    }}
                    title="View Price Comparison"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                ),
              },
            ]}
            data={filteredItems}
            loading={false}
            onEdit={setEditingItem}
            onDelete={setDeletingItem}
            getItemId={(item) => item.item_id!}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />

          {/* Pagination footer */}
          <DataTablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No items found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery || categoryFilter !== 'all' || departmentFilter !== 'all' || stockStatusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first inventory item to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <ModalForm<Item>
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title="Add New Item"
        description="Create a new inventory item."
        fields={itemFields}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit Modal */}
      <ModalForm<Item>
        open={!!editingItem}
        onOpenChange={(open) => !open && setEditingItem(null)}
        title="Edit Item"
        description={`Editing: ${editingItem?.name}`}
        fields={itemFields}
        initialData={editingItem || undefined}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingItem}
        onOpenChange={(open) => !open && setDeletingItem(null)}
        title="Delete Item"
        description={`Are you sure you want to delete "${deletingItem?.name}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* Price Comparison Dialog */}
      <Dialog open={isPriceComparisonOpen} onOpenChange={setIsPriceComparisonOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Price Comparison: {priceComparisonItem?.name}
            </DialogTitle>
            <DialogDescription>
              Compare pricing from different vendors for this item
            </DialogDescription>
          </DialogHeader>

          {priceComparisonLoading && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
              </div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-10 w-full bg-muted rounded animate-pulse" />
                  <div className="h-20 w-full bg-muted rounded animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {!priceComparisonLoading && priceComparisonData && (
            <div className="space-y-4">
              {priceComparisonData.best_price && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">Best Price Available</p>
                      <p className="text-lg font-bold text-green-900">
                        {formatCurrency(priceComparisonData.best_price.unit_price)}
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        from {priceComparisonData.best_price.vendor_name} (MOQ: {priceComparisonData.best_price.minimum_order_quantity || 1})
                      </p>
                    </div>
                    <Badge variant="success">Best Value</Badge>
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-background-subtle">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Vendor</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Unit Price</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">MOQ</th>
                      <th className="px-4 py-3 text-sm font-medium text-text-secondary">Lead Time</th>
                      <th className="px-4 py-3 text-sm font-medium text-text-secondary">Contract</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceComparisonData.vendor_prices.map((vp: any) => (
                      <tr key={vp.vendor_id} className="border-t hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-text-primary">{vp.vendor_name}</p>
                            <p className="text-xs text-text-muted">{vp.vendor_code}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-mono font-medium ${vp.vendor_id === priceComparisonData.best_price?.vendor_id ? 'text-green-600' : ''}`}>
                            {formatCurrency(vp.unit_price)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">{vp.minimum_order_quantity || 1}</td>
                        <td className="px-4 py-3 text-sm">{vp.lead_time_days || '-'} days</td>
                        <td className="px-4 py-3 text-right">
                          {vp.has_contract ? (
                            <Badge variant="success">Active</Badge>
                          ) : (
                            <Badge variant="outline">None</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {priceComparisonData.vendor_prices.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-text-muted">No vendor pricing available for this item.</p>
                  <p className="text-sm text-text-muted mt-1">
                    Price data is populated when purchase orders are created for vendors.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
